/**
 * Der einzige Ort, an dem das Frontend `fetch` gegen die eigene API aufruft. Kapselt
 * `Authorization: Bearer <token>`, `x-client-id`, die JSON-Verarbeitung und die 401-Behandlung -
 * 1:1 zu `api()` (`index.html:5312-5327`), mit zwei benannten Unterschieden:
 *
 * 1. `api()` liefert die rohe `Response` und ueberlaesst jedem Aufrufer das `await r.json()` samt
 *    Statuspruefung; hier gibt `anfrage()` bereits den ausgewerteten Koerper zurueck und wirft bei
 *    jedem nicht-erfolgreichen Status. Das ist kein Verhaltensunterschied gegenueber dem Bestand,
 *    sondern dessen an genau einer Stelle zusammengezogene Wiederholung
 *    (`if (!r.ok) { const err = new Error(result.error || ('HTTP ' + r.status)); err.status = ... }`
 *    stand im Original mehrfach, u. a. `index.html:6100-6103` und `6128-6132`).
 * 2. Der 409 wird als eigener Fehlertyp geworfen, der `current` aus dem Antwortkoerper traegt
 *    (`backend/src/app.ts`, Fehler-Middleware). Der Bestand hatte dafuer keinen Platz - er
 *    versuchte stattdessen zweimal einen Drei-Wege-Merge (`index.html:6149-6162`), der laut Spec
 *    ("Verhaltensaenderung 1") entfaellt.
 *
 * SEIT BEFUND B17 (Nachmessung R5, Task "Sitzung und Strom") persistiert dieses Modul das Token
 * zusaetzlich unter `localStorage['hubSession']` - demselben Schluessel wie im Bestand
 * (`index.html:5273/5713/5731/5321`), damit eine bestehende Sitzung den Umstieg auf die SPA
 * uebersteht. `setzeSitzungsToken()` ist dafuer die EINE Stelle: im Bestand stehen Setzen/Loeschen
 * an VIER Stellen (Anmelden, Abmelden, 401, Toplevel-Lesen beim Laden) je einzeln daneben, hier
 * laufen die ersten drei durch diese eine Funktion (Anmelden: `api/auth.ts#anmelden`; Abmelden:
 * `api/auth.ts#abmelden` und `components/App/App.tsx#abgemeldet`; 401: `anfrage()` unten). Das
 * vierte - der Toplevel-Read beim Laden - ist absichtlich NICHT hier: es gibt in der SPA keinen
 * Skript-Toplevel, der vor jeder Komponente laeuft; stattdessen ruft der Bootstrap-Effekt in
 * `components/App/App.tsx` beim Mounten `sitzungAusSpeicherUebernehmen()` unten, symmetrisch zu
 * `bootstrapSession()` als LETZTER Anweisung des Originalskripts (`index.html:14937`).
 *
 * BEWUSST OHNE try/catch um die `localStorage`-Zugriffe des Sitzungstokens - anders als z. B.
 * `THEME_STORAGE_KEY` in `components/App/App.tsx` oder die Arbeitsplatz-Schluessel in
 * `state/arbeitsplatz.ts`. Das Original sichert `localStorage.getItem('hubSession')`/`.setItem`/
 * `.removeItem` an KEINER der vier Stellen ab (`index.html:5273/5713/5731/5321`) - anders als bei
 * `initTheme()`/`applyTheme()`, die es an ihrer Stelle ausdruecklich tun. Woertlichkeit vor
 * Robustheit: ein blockiertes `localStorage` wuerde hier also wie im Original durchschlagen (beim
 * Anmelden ueber `Anmeldung.tsx`s eigenen try/catch als Sammeltext `FEHLERTEXTE.unerwartet`
 * sichtbar, beim Toplevel-Lesen als Absturz des Bootstrap-Effekts) statt still verschluckt zu werden.
 *
 * Der Verlust wird weiterhin ueber `beiSitzungsverlust` gemeldet - das ist die Naht, an der Task 7
 * `showLogin()`/`updateUserUi()` einhaengt.
 */
import { UploadVerbindungsFehler } from "../lib/upload.ts";
import { clientKennung } from "./clientKennung.ts";

/** Fehler mit HTTP-Status und ausgewertetem Antwortkoerper. */
export class ApiFehler extends Error {
  readonly status: number;
  readonly koerper: unknown;

  constructor(status: number, meldung: string, koerper: unknown) {
    super(meldung);
    this.name = "ApiFehler";
    this.status = status;
    this.koerper = koerper;
  }
}

/**
 * 409: der Datensatz wurde zwischenzeitlich fremd geaendert. `aktuellerStand` ist das `current`-Feld
 * der Antwort - der bereits mitgelieferte Serverstand, mit dem der Konflikt-Banner ohne zweite
 * Anfrage auskommt (siehe `backend/src/versioning/rowVersion.ts`).
 */
export class KonfliktFehler extends ApiFehler {
  readonly aktuellerStand: unknown;

  constructor(meldung: string, koerper: unknown, aktuellerStand: unknown) {
    super(409, meldung, koerper);
    this.name = "KonfliktFehler";
    this.aktuellerStand = aktuellerStand;
  }
}

/** 401: Sitzung fehlt oder ist abgelaufen. Wortlaut siehe `SITZUNG_FEHLT_MELDUNG` im Backend. */
export class SitzungsFehler extends ApiFehler {
  constructor(meldung: string, koerper: unknown) {
    super(401, meldung, koerper);
    this.name = "SitzungsFehler";
  }
}

/**
 * `fetch` selbst hat geworfen: kein Netz, DNS, abgebrochene Verbindung. Es gibt keine Antwort und
 * damit keinen Status. `ursache` ist der urspruengliche Fehler (im Browser ein `TypeError`).
 *
 * Ein Abbruch ueber `signal` (`AbortError`) und ein Zeitablauf (`TimeoutError`) sind BEWUSST keine
 * `VerbindungsFehler`: `anfrage()` reicht sie unveraendert durch, und `lib/fehlertext.ts` entscheidet
 * an deren `name`. Die `message` ist Programmierer-Information und wird nie angezeigt.
 */
export class VerbindungsFehler extends Error {
  readonly ursache: unknown;

  constructor(ursache: unknown) {
    super(`Keine Verbindung: ${ursache instanceof Error ? ursache.message : String(ursache)}`);
    this.name = "VerbindungsFehler";
    this.ursache = ursache;
  }
}

/**
 * Die Antwort kam mit Erfolgsstatus (2xx), ihr Koerper riss aber ab (Verbindung weg, Proxy hat getrennt): `text()` lehnte
 * ab. Der Server HAT die Anfrage bearbeitet - ob sie gelang, steht nur im verlorenen Koerper. Bisher lieferte `anfrage()`
 * hier `undefined` als scheinbaren Erfolg: ein Ladefehler erschien als leere Ansicht, ein Schreiben als gelungen.
 *
 * Bewusst KEIN `ApiFehler`: es gibt keinen Fehlstatus, und die Weichen auf `instanceof ApiFehler` (404 in
 * `anfrageOderNull`, 409-Konflikt) duerfen nicht greifen. `methode` entscheidet den Nutzertext (`lib/fehlertext.ts`):
 * beim Lesen genuegt "neu laden", bei jeder anderen Methode kann das Schreiben gelungen sein.
 */
export class AntwortUnvollstaendigFehler extends Error {
  readonly methode: string;
  readonly status: number;

  constructor(methode: string, status: number) {
    super(`Antwort unvollstaendig: ${methode} lieferte ${status}, der Koerper riss ab`);
    this.name = "AntwortUnvollstaendigFehler";
    this.methode = methode;
    this.status = status;
  }
}

/** `name` eines Fehlerobjekts, wenn es einen Text hat - die EINE Fassung fuer `anfrage()` und `lib/fehlertext.ts`. */
export function nameVon(fehler: unknown): string | undefined {
  if (typeof fehler !== "object" || fehler === null) return undefined;
  const name = (fehler as { name?: unknown }).name;
  return typeof name === "string" ? name : undefined;
}

/**
 * Der Servertext einer JSON-Antwort `{ error: "…" }` - nur, wenn er vorhanden und nicht leer ist. Die EINE Fassung
 * der Regel fuer `meldungAus` unten (Meldung des `ApiFehler`) und `lib/fehlertext.ts` (Nutzertext). Ausnahme:
 * `lib/upload.ts` (`serverText`) fuehrt noch eine woertliche Kopie, bis das Upload-Umfeld wieder angefasst wird.
 */
export function servertextAus(koerper: unknown): string | null {
  if (typeof koerper !== "object" || koerper === null) return null;
  const text = (koerper as { error?: unknown }).error;
  return typeof text === "string" && text !== "" ? text : null;
}

/** `hubSession` (index.html:5273/5713/5731/5321) - derselbe Schluessel wie im Bestand. */
const SITZUNGS_SPEICHER_SCHLUESSEL = "hubSession";

let token: string | null = null;
let sitzungsverlustHandler: (() => void) | null = null;

/**
 * Setzt (oder loescht) das Sitzungstoken UND spiegelt das nach `localStorage` - 1:1 zu den drei
 * Bestandsstellen, die SESSION_TOKEN und `localStorage['hubSession']` gemeinsam aendern (Anmelden
 * `:5713`, Abmelden `:5731`, 401 `:5321`, siehe Kopfkommentar oben).
 */
export function setzeSitzungsToken(neuesToken: string | null): void {
  token = neuesToken;
  if (neuesToken) {
    localStorage.setItem(SITZUNGS_SPEICHER_SCHLUESSEL, neuesToken);
  } else {
    localStorage.removeItem(SITZUNGS_SPEICHER_SCHLUESSEL);
  }
}

export function sitzungsToken(): string | null {
  return token;
}

/**
 * Uebernimmt ein zuvor gespeichertes Token aus `localStorage` als aktuelles Token und liefert es
 * zurueck (`null`, wenn keins gespeichert ist). 1:1 zu `index.html:5273`
 * (`let SESSION_TOKEN = localStorage.getItem('hubSession') || '';`) - dort ein Skript-Toplevel-
 * Ausdruck, hier eine Funktion, weil die SPA keinen Toplevel kennt, der vor jeder Komponente
 * laeuft. Aufrufer: der Bootstrap-Effekt in `components/App/App.tsx`, als ALLERERSTES beim Mounten
 * - symmetrisch zu `bootstrapSession()` als letzter Anweisung des Originalskripts
 * (`index.html:14937`). Schreibt bewusst NICHT zurueck nach `localStorage` (der Wert steht dort
 * bereits) - nur `setzeSitzungsToken()` ist die schreibende Seite.
 */
export function sitzungAusSpeicherUebernehmen(): string | null {
  const gespeichert = localStorage.getItem(SITZUNGS_SPEICHER_SCHLUESSEL);
  token = gespeichert;
  return gespeichert;
}

/**
 * Registriert, was bei einem 401 geschehen soll (Bestand: Sitzung verwerfen, `updateUserUi()`,
 * `showLogin()` - `index.html:5317-5324`). Rueckgabe: eine Funktion, die die Registrierung wieder
 * aufhebt, damit ein Test sie nicht global stehen laesst.
 */
export function beiSitzungsverlust(handler: (() => void) | null): () => void {
  const vorher = sitzungsverlustHandler;
  sitzungsverlustHandler = handler;
  return () => {
    sitzungsverlustHandler = vorher;
  };
}

export type Methode = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface AnfrageOptionen {
  methode?: Methode;
  /**
   * Wird als JSON gesendet. `undefined` heisst: kein Koerper. Dateiuploads (`FormData`) gehen NICHT
   * hierueber, sondern ueber `ladeHoch()`: eine `FormData` wuerde hier zu `{}` stringifiziert.
   */
  koerper?: unknown;
  signal?: AbortSignal;
  /**
   * `true`: diese Anfrage schickt die Anwendung von selbst (SSE-Nachladen, Timer, Wiederholung) -
   * der Mensch hat nichts getan. Sie traegt dann `x-bestellhub-hintergrund: 1`, und das Backend
   * schreibt fuer sie `users.last_seen_at` nicht ("aktiv" heisst: der Mensch hat etwas getan, ein
   * nur offener Tab zaehlt nicht). Fehlt die Angabe, gilt die Anfrage als Handlung des Menschen -
   * ausser der Tab ist verborgen, siehe `standardKopfzeilen`.
   */
  hintergrund?: boolean;
}

/**
 * Kopfname, mit dem eine automatische Anfrage sich als solche ausweist (Vertrag mit dem Backend:
 * Wert `"1"`, das Backend schreibt dafuer `users.last_seen_at` nicht).
 */
export const HINTERGRUND_KOPFZEILE = "x-bestellhub-hintergrund";

/**
 * `true`, wenn der Tab gerade verborgen ist. `document` fehlt ausserhalb des Browsers (SSR, Node-Tests
 * ohne jsdom) - dort gibt es keinen verborgenen Tab, also `false`.
 */
function tabVerborgen(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

/**
 * Die Kopfzeilen jedes Aufrufs. `x-client-id` geht bewusst auch an lesende Aufrufe: die Kennung ist
 * die des Tabs, nicht die des Schreibvorgangs, und der SSE-Strom schickt beim Verbinden dieselbe -
 * eine Unterscheidung nach Methode waere eine zusaetzliche Regel ohne Nutzen.
 *
 * `hintergrund` (siehe `AnfrageOptionen.hintergrund`): setzt `x-bestellhub-hintergrund: 1`. Der Kopf
 * geht AUSSERDEM an jede Anfrage, die aus einem verborgenen Tab abgeht - was ein Mensch nicht sieht,
 * kann er nicht angestossen haben. Das Auffangnetz sitzt hier und nicht in `anfrage()`, damit es fuer
 * alle Wege gilt, die ihre Kopfzeilen hier holen: `anfrage()`, `anfrageOderNull()`,
 * `holeBinaerMitStatus()` und den SSE-Aufbau.
 */
export function standardKopfzeilen(
  mitInhaltstyp: boolean,
  hintergrund = false,
): Record<string, string> {
  const kopfzeilen: Record<string, string> = { "x-client-id": clientKennung() };
  if (token) kopfzeilen["Authorization"] = `Bearer ${token}`;
  if (mitInhaltstyp) kopfzeilen["Content-Type"] = "application/json";
  if (hintergrund || tabVerborgen()) kopfzeilen[HINTERGRUND_KOPFZEILE] = "1";
  return kopfzeilen;
}

/**
 * Wertet einen Antworttext aus: leer ist `undefined`, JSON wird geparst, alles andere bleibt roh.
 * Die EINE Auswertung fuer `anfrage()` (mit dem Text aus `koerperLesen`) und `ladeHoch()` (mit
 * `xhr.responseText`).
 */
function koerperAusText(text: string): unknown {
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch { // still: Nicht-JSON (z. B. ein Proxy-Fehlertext) wird roh weitergereicht statt geworfen; der Aufrufer bekommt ihn ueber ApiFehler.koerper, fehlertext() wertet ihn aus
    return text;
  }
}

/**
 * Der Antworttext, oder `null`, wenn der Koerper nicht lesbar war (Strom abgerissen, Abbruch). Was `null` bedeutet,
 * entscheidet `anfrage()`: ein Abbruch ueber `signal` bleibt ein Abbruch, ein 2xx wird zum `AntwortUnvollstaendigFehler`,
 * ein Fehlstatus bleibt der `ApiFehler` seines Status.
 */
async function koerperLesen(antwort: Response): Promise<string | null> {
  try {
    return await antwort.text();
  } catch { // still: `null` meldet den abgerissenen Koerper an anfrage(), die daraus je nach Status und Signal den passenden Fehler wirft
    return null;
  }
}

function meldungAus(koerper: unknown, status: number): string {
  return servertextAus(koerper) ?? `HTTP ${status}`;
}

/**
 * Bildet aus Status und ausgewertetem Koerper den Fehler eines nicht-erfolgreichen Aufrufs - die EINE
 * Stelle fuer `anfrage()` und `ladeHoch()`, damit beide Wege dieselben Fehlerklassen werfen:
 * `SitzungsFehler` (401), `KonfliktFehler` (409), `ApiFehler` (alles uebrige).
 *
 * Mit Seiteneffekt bei 401: 1:1 zu index.html:5317-5324 wird die Sitzung verworfen (seit B17 inkl.
 * localStorage, deshalb ueber setzeSitzungsToken() statt einer rohen Zuweisung) und die Oberflaeche
 * benachrichtigt, bevor der Aufrufer den Fehler wirft.
 */
function fehlerAus(status: number, inhalt: unknown): ApiFehler {
  if (status === 401) {
    setzeSitzungsToken(null);
    sitzungsverlustHandler?.();
    return new SitzungsFehler(meldungAus(inhalt, 401), inhalt);
  }
  if (status === 409) {
    const aktuellerStand =
      typeof inhalt === "object" && inhalt !== null
        ? (inhalt as { current?: unknown }).current
        : undefined;
    return new KonfliktFehler(meldungAus(inhalt, 409), inhalt, aktuellerStand);
  }
  return new ApiFehler(status, meldungAus(inhalt, status), inhalt);
}

/**
 * Ein API-Aufruf. Liefert den ausgewerteten Antwortkoerper und wirft bei jedem nicht-erfolgreichen
 * Status - `SitzungsFehler` (401), `KonfliktFehler` (409) oder `ApiFehler` (alles uebrige). Scheitert
 * schon `fetch` (keine Antwort), kommt ein `VerbindungsFehler`; ein Abbruch ueber `signal` bleibt
 * unveraendert (`AbortError`/`TimeoutError`). Reisst bei einem 2xx der Antwortkoerper ab, kommt ein
 * `AntwortUnvollstaendigFehler` mit der Methode (nie `undefined` als Erfolg).
 */
export async function anfrage<T>(pfad: string, optionen: AnfrageOptionen = {}): Promise<T> {
  const { methode = "GET", koerper, signal, hintergrund = false } = optionen;
  const hatKoerper = koerper !== undefined;
  // Kopfzeilen und Koerper VOR dem `try`: ein Fehler beim Aufbau (z. B. ein Zirkelbezug in
  // `JSON.stringify`) ist ein Programmfehler und kein Netzfehler. Eine `FormData` geht NICHT hierueber,
  // sondern ueber `ladeHoch()` (siehe `AnfrageOptionen.koerper`).
  const kopfzeilen = standardKopfzeilen(hatKoerper, hintergrund);
  const body = hatKoerper ? JSON.stringify(koerper) : undefined;
  let antwort: Response;
  try {
    antwort = await fetch(pfad, { method: methode, headers: kopfzeilen, body, signal });
  } catch (fehler) {
    // Ein Abbruch ueber `signal` bleibt ein Abbruch - mit seinem `reason`, damit ein Zeitablauf
    // (`AbortSignal.timeout`, `TimeoutError`) vom Abbruch durch den Aufrufer (`AbortError`) zu
    // unterscheiden bleibt. Nur ein Fehler ohne Antwort und ohne Abbruch ist ein Netzfehler.
    if (signal?.aborted) throw signal.reason ?? fehler;
    if (nameVon(fehler) === "AbortError") throw fehler;
    throw new VerbindungsFehler(fehler);
  }

  const text = await koerperLesen(antwort);

  // Ein Abbruch waehrend des Koerperlesens: `koerperLesen` faengt den Fehler des Stroms ab. Wer sein
  // `signal` abbricht, will eine Ablehnung sehen - weder einen leeren Erfolg noch einen Ladefehler.
  if (signal?.aborted) {
    throw signal.reason ?? new DOMException("Aborted", "AbortError");
  }

  if (text === null) {
    // Ein 2xx mit abgerissenem Koerper ist KEIN Erfolg: `undefined` erschiene als leere Ansicht bzw. als
    // gelungenes Schreiben (Fehleranzeige Paket 2). Bei einem Fehlstatus ist der Koerper entbehrlich: der
    // Status traegt die Meldung (`ApiFehler` "HTTP <status>"), und 401/409 greifen wie bei einem leeren Koerper.
    if (antwort.ok) throw new AntwortUnvollstaendigFehler(methode, antwort.status);
    throw fehlerAus(antwort.status, undefined);
  }

  const inhalt = koerperAusText(text);
  // 401 und 409 sind nie `ok`; `fehlerAus` unterscheidet sie von den uebrigen Fehlstatus.
  if (!antwort.ok) throw fehlerAus(antwort.status, inhalt);
  return inhalt as T;
}

export interface LadeHochOptionen {
  /**
   * Anteil der bereits gesendeten Bytes, 0 bis 1 (`upload.onprogress`). Nach der erfolgreichen
   * Antwort folgt genau einmal `1`. Der Browser meldet 1 schon, wenn die letzten Bytes SENDEN sind -
   * der Server arbeitet dann noch; erst die Antwort beendet den Upload.
   */
  onFortschritt?: (anteil: number) => void;
  signal?: AbortSignal;
}

/** Der Fehler eines abgebrochenen Aufrufs: der Grund des Signals, sonst ein `AbortError`. */
function abbruchFehler(signal: AbortSignal | undefined): unknown {
  return signal?.aborted && signal.reason !== undefined
    ? signal.reason
    : new DOMException("Aborted", "AbortError");
}

/**
 * Laedt ein `FormData` per `POST` hoch - der einzige Weg fuer Uploads (`anfrage()` kennt keinen
 * `FormData`-Koerper), ueber `XMLHttpRequest`, weil nur das den Sendefortschritt meldet (`fetch`
 * kennt kein Aequivalent zu `upload.onprogress`). Die uebrige Vertragsseite ist dieselbe wie bei
 * `anfrage()`:
 *
 * - Kopfzeilen aus `standardKopfzeilen(false)` (`x-client-id`, `Authorization`) und KEIN
 *   `Content-Type`: der Browser haengt `multipart/form-data; boundary=...` selbst an.
 * - Der Koerper wird wie bei `anfrage()` ausgewertet (leer, JSON oder roher Text) und der Fehler an
 *   derselben Stelle gebildet (`fehlerAus`): 401 verwirft die Sitzung, 409 wird `KonfliktFehler`.
 * - Kein Zeitlimit auf der Clientseite: grosse Dateien auf langsamen Leitungen duerfen so lange
 *   brauchen, wie der Server ihnen laesst (`server.requestTimeout`, 15 Minuten).
 *
 * Ohne HTTP-Antwort (Netzfehler, `status === 0`) wirft sie `UploadVerbindungsFehler`
 * (`lib/upload.ts`). Ein abgebrochenes `signal` bricht die Anfrage ab und lehnt mit dessen Grund
 * ab (`AbortError`).
 */
export function ladeHoch<T>(
  pfad: string,
  formular: FormData,
  optionen: LadeHochOptionen = {},
): Promise<T> {
  const { onFortschritt, signal } = optionen;
  return new Promise<T>((aufloesen, ablehnen) => {
    if (signal?.aborted) {
      ablehnen(abbruchFehler(signal));
      return;
    }

    const xhr = new XMLHttpRequest();
    const beiAbbruch = (): void => xhr.abort();
    const aufraeumen = (): void => signal?.removeEventListener("abort", beiAbbruch);

    xhr.open("POST", pfad);
    for (const [name, wert] of Object.entries(standardKopfzeilen(false))) {
      xhr.setRequestHeader(name, wert);
    }

    xhr.upload.onprogress = (ereignis) => {
      if (ereignis.lengthComputable && ereignis.total > 0) {
        onFortschritt?.(Math.min(1, ereignis.loaded / ereignis.total));
      }
    };
    xhr.onload = () => {
      aufraeumen();
      if (xhr.status === 0) {
        ablehnen(new UploadVerbindungsFehler());
        return;
      }
      const inhalt = koerperAusText(xhr.responseText);
      if (xhr.status < 200 || xhr.status >= 300) {
        ablehnen(fehlerAus(xhr.status, inhalt));
        return;
      }
      // Erst erfuellen, dann melden: wirft der Rueckruf, bleibt der Aufrufer sonst ewig im Ladezustand.
      aufloesen(inhalt as T);
      onFortschritt?.(1);
    };
    xhr.onerror = () => {
      aufraeumen();
      ablehnen(new UploadVerbindungsFehler());
    };
    xhr.onabort = () => {
      aufraeumen();
      ablehnen(abbruchFehler(signal));
    };

    signal?.addEventListener("abort", beiAbbruch, { once: true });
    xhr.send(formular);
  });
}

/**
 * Laedt einen binaeren Antwortkoerper (`Blob`) ueber denselben authentifizierten Weg wie `anfrage()`
 * - mit `standardKopfzeilen(false)`, also inklusive `Authorization: Bearer <token>`. Auftrag 5: ein
 * `<img src="/api/jtl/articles/…/image">` kann keinen selbstgesetzten Header mitschicken, das
 * Backend liest den Token aber ausschliesslich aus diesem Header
 * (`backend/src/http/middleware/authenticate.ts:50`) - jeder Bildabruf muss deshalb ueber `fetch`
 * mit Header laufen, das Ergebnis als `Blob` genommen und ueber `URL.createObjectURL` angezeigt
 * werden (`components/Artikelbild.tsx`).
 *
 * BEWUSST OHNE die 401-Behandlung von `anfrage()`: ein Bild ist Beiwerk. Ein 401 waere hier zwar
 * moeglich (echte Sitzungsende, nicht der urspruengliche Fehler), darf aber weder die Sitzung
 * verwerfen noch `sitzungsverlustHandler` ausloesen - beides wuerde die Anwendung deshalb abmelden,
 * weil ausgerechnet ein Thumbnail nicht laden konnte.
 *
 * Liefert zusaetzlich zum `Blob` den HTTP-Status mit (`status: null` bei einem Netzfehler - `fetch`
 * selbst wirft, kein `antwort.status` vorhanden, z. B. offline oder ein abgebrochener Koerper beim
 * Lesen von `.blob()`): der Aufrufer (`components/Artikelbild.tsx#useAuthentifiziertesBild`) muss
 * zwischen einem ECHTEN 404 (kein Bild in JTL, der Normalfall, sitzungsweit cachefaehig) und JEDEM
 * anderen Fehlstatus (401/500/Netzfehler - kann sich spaeter erledigen, z. B. Sitzung erneuert, Netz
 * wieder da, und darf darum NICHT gecacht werden) unterscheiden koennen. Deshalb steckt `.blob()`
 * bewusst MIT im `try`, nicht erst danach.
 *
 * Bis zum sitzungsweiten 404-Cache (`components/Artikelbild.tsx#bekanntBildlos`) gab es hier ein
 * `holeBinaer()` ohne Status, das einheitlich `Blob | null` lieferte - Grep ergibt inzwischen KEINEN
 * Aufrufer mehr ausser `components/Artikelbild.tsx`, das die Unterscheidung braucht: ersatzlos
 * entfernt statt als totes Nebengleis stehen gelassen.
 */
export async function holeBinaerMitStatus(
  pfad: string,
): Promise<{ blob: Blob | null; status: number | null }> {
  try {
    const antwort = await fetch(pfad, { headers: standardKopfzeilen(false) });
    if (!antwort.ok) return { blob: null, status: antwort.status };
    return { blob: await antwort.blob(), status: antwort.status };
  } catch { // still: der Netzfehler steht im Rueckgabewert (status: null); Artikelbild zeigt den Platzhalter und cacht ihn nicht, ladeUndSpeichereDatei() macht daraus unten einen VerbindungsFehler
    return { blob: null, status: null };
  }
}

/**
 * Laedt eine Datei ueber `holeBinaerMitStatus()` oben (authentifiziert, Bearer-Header) und stoesst
 * danach den Browser-eigenen Speichern-Dialog an - Auftrag "DSV-Belege (Zustellbeleg + Rechnung) in
 * der Bestellung anzeigen": ein einfaches `<a href=".../datei" download>` kann den
 * Authorization-Header nicht mitschicken (derselbe Grund wie am Kopfkommentar von
 * `holeBinaerMitStatus`), ein `<a>` auf eine per `Blob`/`URL.createObjectURL` erzeugte Objekt-URL
 * dagegen schon. Der `download`-Dateiname kommt dabei NICHT aus der Objekt-URL (die traegt keinen),
 * sondern wird hier explizit gesetzt - unabhaengig vom `content-disposition`-Kopf der Antwort, den
 * `fetch()` beim Zusammenbauen der Objekt-URL ohnehin nicht auswertet.
 *
 * Loest bei Erfolg ohne Wert auf und WIRFT bei jedem Fehlstatus/Netzfehler (Fehleranzeige Paket 1,
 * Task 5; vorher `true`/`false`, und `false` verschluckte die Ursache): bei einem Fehlstatus einen
 * `ApiFehler(status, "HTTP <status>", undefined)`, bei einem Netzfehler (`status === null`) einen
 * `VerbindungsFehler(null)`. Ein 401 bleibt hier BEWUSST ein `ApiFehler` und wird kein
 * `SitzungsFehler`: `holeBinaerMitStatus` loest keine Anmeldemaske aus (siehe dort), und
 * `fehlertext(SitzungsFehler)` ergaebe `null` - der Download scheiterte dann still. Der Aufrufer
 * (`api/useDateiDownload.ts` inline, sonst `meldeFehler`) macht den Text ueber `fehlertext()`.
 *
 * `URL.revokeObjectURL()` laeuft ERST einen Makrotask spaeter (Nachbesserung, Pruefer-Befund "Sollte"
 * 7, 05.09.2026): direkt im selben Task wie `anker.click()` widerrufen, ist der eigentliche
 * Download in manchen Browsern (Firefox/Safari, dokumentiertes Verhalten) noch nicht tatsaechlich
 * gestartet - der Widerruf kann ihn dann abbrechen, ohne dass diese Funktion (die `click()` selbst
 * wirft nie) davon je erfaehrt. `setTimeout(..., 0)` reicht die Freigabe in den naechsten Makrotask
 * weiter, NACHDEM der Browser den Download-Vorgang tatsaechlich aufgenommen hat.
 */
export async function ladeUndSpeichereDatei(pfad: string, dateiname: string): Promise<void> {
  const { blob, status } = await holeBinaerMitStatus(pfad);
  if (!blob) {
    if (status === null) throw new VerbindungsFehler(null);
    throw new ApiFehler(status, `HTTP ${status}`, undefined);
  }
  const objectUrl = URL.createObjectURL(blob);
  const anker = document.createElement("a");
  anker.href = objectUrl;
  anker.download = dateiname;
  document.body.appendChild(anker);
  anker.click();
  anker.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

/** Wie `anfrage`, liefert bei 404 aber `null` statt zu werfen (fuer "gibt es den noch?"-Aufrufe). */
export async function anfrageOderNull<T>(
  pfad: string,
  optionen: AnfrageOptionen = {},
): Promise<T | null> {
  try {
    return await anfrage<T>(pfad, optionen);
  } catch (fehler) {
    if (fehler instanceof ApiFehler && fehler.status === 404) return null;
    throw fehler;
  }
}

/**
 * ============================ Die Annahme ueber die Domaenen-API ============================
 *
 * DIE GENAUE FORM DER DOMAENEN-ENDPUNKTE STEHT ERST MIT PHASE 4a FEST (Phase-6-Plan, "Was dieser
 * Plan nicht weiss", Zeile 1). Bis dahin gilt die REST-Form, die die bereits gebauten Endpunkte
 * aus Phase 1 vorzeichnen (`backend/src/app.ts`, Antwortform-Konvention):
 *
 *   GET    <basis>            -> blankes Array
 *   GET    <basis>/<id>       -> blankes Objekt, 404 wenn es den Datensatz nicht (mehr) gibt
 *   POST   <basis>            -> 201 + blankes Objekt
 *   PUT    <basis>/<id>       -> 200 + blankes Objekt; `rowVersion` im KOERPER (wie
 *                                PATCH /api/config/:key, der einzige gebaute Versionsendpunkt)
 *   DELETE <basis>/<id>       -> 200 + { ok: true }
 *   409                       -> { error, current }
 *
 * Diese Funktion ist die EINE Stelle, die das weiss. Weicht Phase 4a ab, wird sie umgeschrieben -
 * kein Modul in `api/`, kein Reduzierer, keine Komponente und kein Komponententest aendert sich
 * dadurch.
 *
 * ---------------------------------------------------------------------------------------------
 * NACHGEMESSEN (Task 5a): fuer BESTELLUNGEN stimmt die Annahme NICHT. `PUT /api/orders/:id` gibt es
 * nicht (`backend/src/http/endpunktMatrix.ts:50-70`); geschrieben wird ueber fuenf
 * berechtigungsgebundene Feldgruppen, `PATCH /api/orders/:id/<gruppe>` mit `{ rowVersion, felder }`
 * in snake_case. `api/bestellungen.ts` benutzt `ressource().aendern` deshalb NICHT mehr und baut
 * seinen Schreibaufruf ueber `anfrage()` selbst - siehe dortiger Kopfkommentar.
 *
 * NACHGEMESSEN (Fix-Runde 1 zu Task 5a): hier stand "fuer `api/lieferanten.ts` und
 * `api/kurssicherung.ts` ist die Annahme UNGEPRUEFT (nicht widerlegt, aber auch nicht belegt)".
 * Das war eine Verharmlosung - der Task-5a-Bericht fuehrte fuer dieselben Routen bereits eine
 * MESSUNG. Der gemessene Stand, `endpunktMatrix.ts` Zeile fuer Zeile:
 *
 *   `PUT /api/suppliers/:id`   EXISTIERT NICHT. Unter /api/suppliers gibt es GENAU ZWEI Endpunkte:
 *                              `GET /api/suppliers/:kLieferant` (Zeile 105) und
 *                              `PATCH /api/suppliers/:kLieferant/extras` (Zeile 106). Keine Liste,
 *                              kein POST, kein DELETE.
 *   `PUT /api/fx-deals/:id`    EXISTIERT NICHT. Geaendert wird per PATCH mit `{ rowVersion, felder }`
 *                              (Zeile 109, `routes/fxDeals.ts:127-160`); ein
 *                              `GET /api/fx-deals/:dealId` gibt es ebenfalls nicht.
 *
 * `aendern` hat deshalb KEINEN Benutzer mehr - kein Modul unter `frontend/src` weist `zugriff.aendern`
 * einer exportierten Funktion zu. `anlegen` dagegen HAT welche: `api/bestellungen.ts#bestellungAnlegen`
 * (gerufen aus `App.tsx#neueBestellungAnlegen`, Schritt 2 des Anlagepfads) und
 * `api/kurssicherung.ts#kurssicherungAnlegen` (gerufen aus `App.tsx#kurssicherungSpeichern`) - beide
 * treffen `POST <basis>` genau so, wie diese Funktion es baut. Die Funktionen bleiben hier stehen,
 * weil `ressource()` die Form beschreibt, die Phase 1 vorzeichnet, und weil ein kuenftiges Modul sie
 * treffen kann - wer `aendern` benutzt, schlaegt vorher in `endpunktMatrix.ts` nach.
 * ---------------------------------------------------------------------------------------------
 */
export interface Ressource<T> {
  /** `hintergrund`: siehe `AnfrageOptionen.hintergrund` - nur fuer automatische Aufrufe (SSE-Nachladen). */
  liste(signal?: AbortSignal, hintergrund?: boolean): Promise<T[]>;
  einzeln(id: string, signal?: AbortSignal, hintergrund?: boolean): Promise<T | null>;
  anlegen(daten: Partial<T>): Promise<T>;
  aendern(id: string, daten: Partial<T>, rowVersion: number): Promise<T>;
  loeschen(id: string, rowVersion: number): Promise<void>;
}

export function ressource<T>(basispfad: string): Ressource<T> {
  const pfadZu = (id: string) => `${basispfad}/${encodeURIComponent(id)}`;
  return {
    liste: (signal, hintergrund) => anfrage<T[]>(basispfad, { signal, hintergrund }),
    einzeln: (id, signal, hintergrund) => anfrageOderNull<T>(pfadZu(id), { signal, hintergrund }),
    anlegen: (daten) => anfrage<T>(basispfad, { methode: "POST", koerper: daten }),
    aendern: (id, daten, rowVersion) =>
      anfrage<T>(pfadZu(id), { methode: "PUT", koerper: { ...daten, rowVersion } }),
    loeschen: async (id, rowVersion) => {
      await anfrage<{ ok: true }>(pfadZu(id), { methode: "DELETE", koerper: { rowVersion } });
    },
  };
}
